import React, { useState } from 'react';
import { Form, Input, Button, Upload, Row, Col, Card, message, Spin, Pagination, Select, Checkbox, Modal, Tag, Radio } from 'antd';
import { UploadOutlined, SearchOutlined } from '@ant-design/icons';
import { searchByImage as apiSearchByImage } from '../services/api';
import './SearchPage.css';
import MobileSearchPage from './MobileSearchPage';
import { useMode } from '../context/ModeContext';

const { Option } = Select;

const DesktopSearchPage: React.FC = () => {
    // Component State
    const [searchResults, setSearchResults] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalResults, setTotalResults] = React.useState(0);
    const [allTags, setAllTags] = React.useState<string[]>([]);
    const [selectedTags, setSelectedTags] = React.useState<string[]>([]);

    React.useEffect(() => {
        (async () => {
            try {
                const data = await (await import('../services/api')).getAllTags();
                const tags = Array.isArray(data?.tags) ? data.tags : (Array.isArray(data) ? data : []);
                setAllTags(tags.map((t: any) => String(t)).filter(Boolean));
            } catch {}
        })();
    }, []);
    
    // Handlers
    const handleImageSearch = async (values: any) => {
        if (!values.image || values.image.length === 0) {
            message.error('Please upload an image for the search.');
            return;
        }

        setLoading(true);
        try {
            // 直接调用封装好的API函数，而不是手动创建FormData
            const imageFile = values.image[0].originFileObj;
            const data = await apiSearchByImage(imageFile, 20, 0.6, 'baidu_cloud', { strictBaidu: true });
            const results = Array.isArray(data?.results) ? data.results : [];
            const filtered = selectedTags.length
              ? results.filter((r: any) => Array.isArray(r?.tags) && selectedTags.every(t => r.tags.includes(t)))
              : results;
            setSearchResults(filtered);
            setTotalResults(filtered.length);
        } catch (error) {
            message.error('Image search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    
    // Ant Design Form normalize function
    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e && e.fileList;
    };

    // Pagination
    const pageSize = 12;
    const paginatedResults = searchResults.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="search-page-container">
            
            <Card className="search-card">
                <Form onFinish={handleImageSearch} layout="vertical">
                    <Form.Item name="image" label="上传图片" valuePropName="fileList" getValueFromEvent={normFile} rules={[{ required: true, message: '请上传图片' }]}>
                        <Upload name="logo" listType="picture" maxCount={1} beforeUpload={() => false}>
                            <Button icon={<UploadOutlined />}>点击上传</Button>
                        </Upload>
                    </Form.Item>
                    <Form.Item label="按标签筛选（可选）">
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="选择标签以更精准地筛选结果"
                            value={selectedTags}
                            onChange={setSelectedTags}
                            options={allTags.map(t => ({ value: t, label: t }))}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={loading} block>
                            搜索
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <div className="search-results-container">
                {loading ? <Spin size="large" /> : (
                    <>
                        <Row gutter={[16, 16]}>
                            {paginatedResults.map((result, index) => {
                                const anyRes: any = result as any;
                                const fp = typeof anyRes.file_path === 'string' ? anyRes.file_path : '';
                                const isRemote = fp.startsWith('http');
                                const isLocalUploaded = fp.startsWith('/uploads/');
                                const hasValidId = typeof anyRes.id === 'number' && anyRes.id > 0;
                                const displaySrc = isRemote
                                    ? fp
                                    : (isLocalUploaded
                                        ? fp
                                        : (hasValidId
                                            ? `/api/images/${anyRes.id}/thumbnail?size=500`
                                            : (result.filename ? `/uploads/${result.filename}` : '')));
                                const previewSrc = isRemote
                                    ? fp
                                    : (result.filename ? `/uploads/${result.filename}` : displaySrc);
                                return (
                                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                    <Card
                                        hoverable
                                        className="result-card"
                                        cover={<img alt={result.filename} src={displaySrc} />}
                                        onClick={() => Modal.info({
                                            title: result.filename,
                                            content: (
                                                <div>
                                                    <img alt={result.filename} src={previewSrc} style={{ width: '100%', marginBottom: '1rem' }} />
                                                    <p><strong>描述:</strong> {result.description}</p>
                                                    <p><strong>标签:</strong></p>
                                                    <div>
                                                        {result.tags.map((tag: string, i: number) => <Tag color="blue" key={i}>{tag}</Tag>)}
                                                    </div>
                                                </div>
                                            ),
                                            width: '80%',
                                        })}
                                    >
                                        <Card.Meta 
                                            title={result.filename} 
                                            description={
                                                <div>
                                                    <p>相似度: {(result.similarity * 100).toFixed(2)}%</p>
                                                    <p>描述: {result.description}</p>
                                                    <div>
                                                      <strong style={{ marginRight: 8 }}>标签:</strong>
                                                      {Array.isArray(result.tags) && result.tags.map((tag: string, i: number) => <Tag color="blue" key={i}>{tag}</Tag>)}
                                                    </div>
                                                </div>
                                            } 
                                        />
                                    </Card>
                                </Col>
                                );
                            })}
                        </Row>
                        {totalResults > pageSize && (
                            <Pagination
                                current={currentPage}
                                pageSize={pageSize}
                                total={totalResults}
                                onChange={setCurrentPage}
                                style={{ marginTop: '2rem', textAlign: 'center' }}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const SearchPage: React.FC = () => {
    const { mode, setMode } = useMode();

    return (
        <div>
            {/* 移除这里的切换按钮 */}
            {mode === 'desktop' ? <DesktopSearchPage /> : <MobileSearchPage />}
        </div>
    );
}

export default SearchPage;
